import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Grid,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DeleteIcon from '@mui/icons-material/Delete';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { Worker } from '../../../types/worker';

interface ShiftManagementProps {
  farmId: string;
  workers: Worker[];
}

interface Shift {
  id: string;
  workerId: string;
  startTime: string;
  endTime: string;
  roleTask: string;
  notes: string;
}

const ShiftManagement: React.FC<ShiftManagementProps> = ({ farmId, workers }) => {
  const [shifts, setShifts] = useState<Shift[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  const [newShift, setNewShift] = useState<Partial<Shift>>({
    startTime: '',
    endTime: '',
    roleTask: ''
  });

  const fetchShifts = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('shift_schedules')
        .select('*')
        .eq('farm_id', farmId)
        .order('start_time', { ascending: false });

      if (error) throw error;

      const mappedShifts = (data || []).map((s: any) => ({
        id: s.id,
        workerId: s.worker_id,
        startTime: s.start_time,
        endTime: s.end_time,
        roleTask: s.role_task,
        notes: s.notes
      }));

      setShifts(mappedShifts);
    } catch (err: any) {
      console.error('Error fetching shifts:', err);
      setError('Failed to load shifts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (farmId) fetchShifts();
  }, [farmId]);

  const handleSubmit = async () => {
    try {
      if (!newShift.workerId || !newShift.startTime || !newShift.endTime) {
        setError('Please fill in all required fields');
        return;
      }

      const { error } = await supabase
        .from('shift_schedules')
        .insert([{
          farm_id: farmId,
          worker_id: newShift.workerId,
          start_time: newShift.startTime,
          end_time: newShift.endTime,
          role_task: newShift.roleTask,
          notes: newShift.notes
        }]);

      if (error) throw error;
      
      setOpenDialog(false);
      setNewShift({});
      await fetchShifts();
    } catch (err: any) {
      console.error('Error creating shift:', err);
      setError('Failed to create shift');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('shift_schedules').delete().eq('id', id);
      if (error) throw error;
      await fetchShifts();
    } catch (err) {
      console.error('Error deleting shift:', err);
    }
  };

  const getWorkerName = (id: string) => {
    return workers.find(w => w.id === id)?.name || 'Unknown';
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Shift Schedules</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Shift
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Worker</TableCell>
                  <TableCell>Time</TableCell>
                  <TableCell>Task/Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shifts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center">No shifts scheduled</TableCell>
                  </TableRow>
                ) : (
                  shifts.map((shift) => (
                    <TableRow key={shift.id}>
                      <TableCell>{format(new Date(shift.startTime), 'MMM d, yyyy')}</TableCell>
                      <TableCell>{getWorkerName(shift.workerId)}</TableCell>
                      <TableCell>
                        <Chip 
                          icon={<ScheduleIcon />} 
                          label={`${format(new Date(shift.startTime), 'HH:mm')} - ${format(new Date(shift.endTime), 'HH:mm')}`} 
                          size="small" 
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{shift.roleTask || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" color="error" onClick={() => handleDelete(shift.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule Shift</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Worker"
              fullWidth
              value={newShift.workerId || ''}
              onChange={(e) => setNewShift({ ...newShift, workerId: e.target.value })}
            >
              {workers.map((worker) => (
                <MenuItem key={worker.id} value={worker.id}>{worker.name}</MenuItem>
              ))}
            </TextField>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  type="datetime-local"
                  label="Start Time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={newShift.startTime || ''}
                  onChange={(e) => setNewShift({ ...newShift, startTime: e.target.value })}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="datetime-local"
                  label="End Time"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={newShift.endTime || ''}
                  onChange={(e) => setNewShift({ ...newShift, endTime: e.target.value })}
                />
              </Grid>
            </Grid>

            <TextField
              label="Role / Task"
              fullWidth
              value={newShift.roleTask || ''}
              onChange={(e) => setNewShift({ ...newShift, roleTask: e.target.value })}
            />

            <TextField
              label="Notes"
              multiline
              rows={2}
              fullWidth
              value={newShift.notes || ''}
              onChange={(e) => setNewShift({ ...newShift, notes: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Schedule</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ShiftManagement;
